import { Button } from '@carbon/react';
import { OpenPanelFilledLeft, OpenPanelLeft } from '@carbon/react/icons';
import { useAtom } from 'jotai';
import { leftPaneOpenAtom } from './atoms';

/**
 * The left-pane collapse/expand toggle at the LEFT of the center title bar — the mirror of
 * the right panel's `EvalSettingsToggle`, so the two panels collapse symmetrically. An
 * icon-only ghost button whose icon reflects state (filled = pane open, outline = collapsed)
 * and stays reachable when the pane is at zero width (it lives in the header, not the pane).
 *
 * Reads the atom (unlike the write-only `EvalSettingsToggle`) so the icon + tooltip track the
 * open/closed state; this is a single tiny button, so the extra re-render on toggle is trivial.
 *
 * @return {JSX.Element} The left-pane toggle button.
 */
export function LeftPaneToggle() {
  const [open, setOpen] = useAtom(leftPaneOpenAtom);
  // "left panel" (not "evaluations") — the pane holds the Evals tree AND the Page Controls
  // (filters / colors / layout), so a content-specific label would undersell it.
  const label = open ? 'Collapse left panel' : 'Expand left panel';
  return (
    <Button
      hasIconOnly
      size="md"
      kind="ghost"
      onClick={() => setOpen((prev) => !prev)}
      iconDescription={label}
      tooltipAlignment="start"
      tooltipPosition="bottom"
      renderIcon={(props) => {
        // Filled = pane open, outline = collapsed.
        const Icon = open ? OpenPanelFilledLeft : OpenPanelLeft;
        return <Icon size={20} {...props} />;
      }}
    />
  );
}
